/**
 * Locale bundle loaders and fallback chains.
 *
 * Split out of index.js so it can be imported (and tested) without
 * instantiating vue-i18n or touching localStorage/navigator.
 *
 * Only `en-us` is bundled eagerly: it is the last link of every fallback chain,
 * so something must always be renderable. Every other bundle is a webpack
 * dynamic import and ships as its own `locale-<code>` chunk. Adding a locale
 * here is the only wiring needed; languageAssetsParity.test.js fails if a code
 * in Language.js has no loader, or if a loader points at a path that does not
 * resolve.
 */
import enUS from "./en-us.json";

/** The locale that is always present. Last link of every chain. */
export const FALLBACK_LOCALE = "en-us";

/** Messages available synchronously at module import. */
export const eagerMessages = Object.freeze({ [FALLBACK_LOCALE]: enUS });

/**
 * Fallback chains, in vue-i18n's `fallbackLocale` shape so the same table
 * drives both what vue-i18n falls back to and what the loader fetches. Keeping
 * one table means a dialect can never fall back to a bundle nobody loaded.
 */
export const localeFallbackChains = Object.freeze({
  "zh-hk": ["zh-cn", "en-us"], // Cantonese falls back to Mandarin, then English
  "es-mx": ["es-es", "en-us"], // Mexican Spanish falls back to European Spanish, then English
  "fr-ca": ["fr-fr", "en-us"], // Canadian French falls back to France French, then English
  "pt-br": ["pt-pt", "en-us"], // Brazilian Portuguese falls back to European Portuguese, then English
  "en-gb": ["en-us"], // British English falls back to American English
  default: ["en-us"], // Everything else falls back to American English
});

/**
 * code -> () => Promise<moduleOrMessages>. `en-us` resolves from the eager
 * import rather than a dynamic one, so it stays in the main chunk and the map
 * is still complete enough to answer "can this locale be loaded?" for every
 * code.
 *
 * The `webpackChunkName` comments make the emitted chunks identifiable in
 * `dist/js/`, which is what lets the payload claim be verified per locale
 * instead of only as a main-chunk delta.
 */
const rawLocaleLoaders = Object.freeze({
  "en-us": () => Promise.resolve(enUS),
  "en-gb": () => import(/* webpackChunkName: "locale-en-gb" */ "./en-gb.json"),
  "zh-cn": () => import(/* webpackChunkName: "locale-zh-cn" */ "./zh-cn.json"),
  "zh-hk": () => import(/* webpackChunkName: "locale-zh-hk" */ "./zh-hk.json"),
  "es-mx": () => import(/* webpackChunkName: "locale-es-mx" */ "./es-mx.json"),
  "es-es": () => import(/* webpackChunkName: "locale-es-es" */ "./es-es.json"),
  "fr-fr": () => import(/* webpackChunkName: "locale-fr-fr" */ "./fr-fr.json"),
  "fr-ca": () => import(/* webpackChunkName: "locale-fr-ca" */ "./fr-ca.json"),
  de: () => import(/* webpackChunkName: "locale-de" */ "./de.json"),
  ja: () => import(/* webpackChunkName: "locale-ja" */ "./ja.json"),
  ko: () => import(/* webpackChunkName: "locale-ko" */ "./ko.json"),
  "pt-br": () => import(/* webpackChunkName: "locale-pt-br" */ "./pt-br.json"),
  "pt-pt": () => import(/* webpackChunkName: "locale-pt-pt" */ "./pt-pt.json"),
  ru: () => import(/* webpackChunkName: "locale-ru" */ "./ru.json"),
  "ta-in": () => import(/* webpackChunkName: "locale-ta-in" */ "./ta-in.json"),
  it: () => import(/* webpackChunkName: "locale-it" */ "./it.json"),
  hi: () => import(/* webpackChunkName: "locale-hi" */ "./hi.json"),
  vi: () => import(/* webpackChunkName: "locale-vi" */ "./vi.json"),
  tr: () => import(/* webpackChunkName: "locale-tr" */ "./tr.json"),
  nl: () => import(/* webpackChunkName: "locale-nl" */ "./nl.json"),
  pl: () => import(/* webpackChunkName: "locale-pl" */ "./pl.json"),
  id: () => import(/* webpackChunkName: "locale-id" */ "./id.json"),
  uk: () => import(/* webpackChunkName: "locale-uk" */ "./uk.json"),
  el: () => import(/* webpackChunkName: "locale-el" */ "./el.json"),
  sv: () => import(/* webpackChunkName: "locale-sv" */ "./sv.json"),
  no: () => import(/* webpackChunkName: "locale-no" */ "./no.json"),
  da: () => import(/* webpackChunkName: "locale-da" */ "./da.json"),
  fi: () => import(/* webpackChunkName: "locale-fi" */ "./fi.json"),
  "bn-in": () => import(/* webpackChunkName: "locale-bn-in" */ "./bn-in.json"),
  te: () => import(/* webpackChunkName: "locale-te" */ "./te.json"),
  mr: () => import(/* webpackChunkName: "locale-mr" */ "./mr.json"),
});

/**
 * A dynamic `import()` of JSON resolves to a module namespace, so the messages
 * sit under `.default`; the eager `en-us` entry resolves to the messages
 * directly. Unwrap once here so every caller gets the same shape and nobody
 * hands vue-i18n a `{ default: ... }` wrapper (which renders as raw keys, the
 * exact silent failure this unit is guarding against).
 */
function unwrapMessages(loaded) {
  return loaded && typeof loaded === "object" && "default" in loaded ? loaded.default : loaded;
}

/** code -> () => Promise<messages>, always unwrapped. */
export const localeLoaders = Object.freeze(
  Object.fromEntries(
    Object.entries(rawLocaleLoaders).map(([code, load]) => [
      code,
      () => Promise.resolve(load()).then(unwrapMessages),
    ])
  )
);

/** True if `locale` is a code this app can actually load a bundle for. */
export function isLoadableLocale(locale) {
  return typeof locale === "string" && typeof localeLoaders[locale] === "function";
}

/**
 * Every bundle that must be present for `locale` to render fully, most specific
 * first. A dialect needs its parent too: zh-hk -> ["zh-hk", "zh-cn", "en-us"].
 * An unknown code resolves to the fallback alone, so a bad stored value degrades
 * to English rather than to nothing.
 */
export function resolveLocaleChain(locale) {
  if (!isLoadableLocale(locale)) {
    return [FALLBACK_LOCALE];
  }
  const chain = [locale, ...(localeFallbackChains[locale] || localeFallbackChains.default)];
  return [...new Set(chain)].filter(isLoadableLocale);
}
